import React, { memo, useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useThrottle, useMemoizedFn, useThrottleFn } from 'ahooks';
import {
  getSuitMaxNumber,
  getFormatNumberRes,
  defaultTooltipFormatter,
  SYMBOL_ARR,
} from './utils';
import { getFormatNumPlus } from '@gp/utils/common';
import './index.scss';

type IOpt = {
  color?: string,
  barWidth?: number,
  value?: string | number,
};
interface BulletChartPropsType {
  className?: string,
  style?: React.CSSProperties,
  tooltipClassName?: string,
  tooltipStyle?: React.CSSProperties,
  // 刻度分割的段数
  splitCount?: number,
  // 柱子的宽度、颜色没有变化的话只传value就行了
  option: {
    'bgBar'?: IOpt,
    'mainBar'?: IOpt,
    'markLine'?: IOpt,
  },
  // 转换数值成K/M/B/T的方法，用于转换刻度
  formatNumber?: {
    func: Function | null, // 方法体
    // 方法中涉及的参数，其中参数为“_entranceValue”的将视为要转换的值传入的参数位置
    // 不传的话要转换的值将默认放在第一参数的位置传入
    params?: any[],
  } | null,
  minPercentage?: number, // 柱子最小占的百分之几
  tooltipData: any,
  tooltipFormatter?: ((data: any) => any) | null,
  // 水平方向hover的位置超过容器一半时是否浮窗在鼠标的左右位置变化
  isTooltipHalfAdaptPosition?: boolean,
  // 刻度是否保留整数
  isLabelKeepInteger?: boolean,
  // 有负数时0刻度是否居中，是的话分割的段数会向上取偶数
  isCenterZeroLabel?: boolean,
}

const DEFAULT_PROPS: BulletChartPropsType = {
  className: '',
  style: {},
  tooltipClassName: '',
  tooltipStyle: {},
  splitCount: 5,
  option: {},
  formatNumber: null,
  minPercentage: 0.5,
  tooltipData: null,
  tooltipFormatter: null,
  isTooltipHalfAdaptPosition: false,
  isLabelKeepInteger: true,
  isCenterZeroLabel: true,
};

function BulletChart(props: BulletChartPropsType) {

  const {
    className,
    style,
    tooltipClassName,
    tooltipStyle,
    splitCount: propsSplitCount,
    option,
    formatNumber,
    minPercentage,
    tooltipData,
    tooltipFormatter,
    isTooltipHalfAdaptPosition,
    isLabelKeepInteger,
    isCenterZeroLabel,
  } = props;

  // 浮窗容器是否准备好
  const [readyTipBox, setReadyTipBox] = useState<boolean>(false);
  // 负数占比
  const [negativePercentage, setNegativePercentage] = useState<number>(0);
  const [visibleTooltip, setVisibleTooltip] = useState<boolean>(false);
  const [containerPaddingLeft, setContainerPaddingLeft] = useState<number>(0);
  const [containerPaddingRight, setContainerPaddingRight] = useState<number>(0);
  // 容器宽度，不包括左右padding
  const [containerBoxWidth, setContainerBoxWidth] = useState<number>(0);
  const throttledContainerBoxWidth = useThrottle(
    containerBoxWidth,
    { wait: 300 },
  );
  const [left, setLeft] = useState<number | string>(0);
  const [top, setTop] = useState<number | string>(0);
  const xAxisBoxRef = useRef<HTMLUListElement>(null);
  const chartAreaRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLElement>(null);

  // mainBar刻度末显示标签占用的宽度占整个容器的百分比（包含10px与柱子间的间距）
  const mainBarLabelPercent = useMemo(() => {
    if (!throttledContainerBoxWidth) return 0;

    const value = option?.mainBar?.value;
    const span = document.createElement('span');
    span.style.cssText = 'position:absolute;visibility:hidden;font-size:14px;';
    span.innerHTML = getFormatNumPlus(value || '');
    document.body.appendChild(span);
    const labelWidth = span.clientWidth || 0; // label占的宽度
    document.body.removeChild(span);

    return Math?.round(((labelWidth + 10) / throttledContainerBoxWidth) * 10000) / 100;
  }, [option?.mainBar?.value, throttledContainerBoxWidth]);

  // 当有负数，0刻度要求在中间时，分割段数是奇数的话+1
  const splitCount: number = useMemo(() => {
    if (negativePercentage > 0 && isCenterZeroLabel) {
      return ((propsSplitCount as number) % 2 === 0) ? (propsSplitCount as number) : (propsSplitCount as number) + 1;
    } else {
      return propsSplitCount || 5;
    }
  }, [propsSplitCount, isCenterZeroLabel, negativePercentage]);

  // 计算出合理的最大值
  const max = useMemo(() => {
    const a = String(option?.bgBar?.value),
          b = String(option?.mainBar?.value),
          c = String(option?.markLine?.value);
    const strArr = [a, b, c],
          numArr = [Number(a) || 0, Number(b) || 0, Number(c) || 0];
    let tempMax = Math?.max(...numArr);
    // 刚开始这个最大值是否是负数
    // const isNegativeInitTempMax = String(tempMax)?.includes('-');
    // 有负数的情况
    let absMinimumV = 0;
    if (strArr?.join('')?.includes('-')) {
      let minimumV = 0;
      for(let i = 0; i < numArr?.length; i ++) {
        const t = numArr?.[i];
        minimumV = t < minimumV ? t : minimumV;
      }
      absMinimumV = Math?.abs(minimumV);
      // const isAllNegative = isNegativeInitTempMax || tempMax === 0;
      tempMax = isCenterZeroLabel
        ? tempMax >= absMinimumV ? tempMax * 2 : absMinimumV * 2
        : (tempMax + absMinimumV);
    }
    // 数据扩大进行计算，因为正负数两个方向都可能各向上取值各扩大一个刻度，有负数时扩大两个刻度，没有负数时扩大一个刻度
    const expansionToRatio = absMinimumV ? (1 + (1 / splitCount) * 2) : (1 + 1 / splitCount);
    const tempRes = getSuitMaxNumber(
      tempMax * expansionToRatio,
      splitCount,
      true,
      formatNumber,
    );
    let res = tempRes;
    // 计算mainBar和它的标签一起有没有超过边界，超过了的话，最大值要扩大
    if (tempRes > 0) {
      const absMainBarV = Math?.abs(Number(b) || 0) || 0;
      const mainBarWidthPercent = Math?.round((absMainBarV / tempRes) * 10000) / 100;
      const mainBarPercent = mainBarWidthPercent < (minPercentage as number) ? (minPercentage as number) : mainBarWidthPercent;
      const percent = mainBarPercent + mainBarLabelPercent; // mainBar及它的标签一起占的宽度百分比
      const adjustPercent = 0.05; // 多扩大一点防止标签出现在容器边缘或者外面
      // 有负数时
      if (absMinimumV) {
        if (percent > 50) {
          const expansionToRatio = (percent) * 2 / 100 + adjustPercent;
          res = getSuitMaxNumber(
            tempRes * expansionToRatio,
            splitCount,
            true,
            formatNumber,
          );
        }
      } else {
        if (percent > 100) {
          const expansionToRatio = percent / 100 + adjustPercent;
          res = getSuitMaxNumber(
            tempRes * expansionToRatio,
            splitCount,
            true,
            formatNumber,
          );
        }
      }
    }
    // 负数占的比例
    absMinimumV && setNegativePercentage(absMinimumV / res);

    return res;
  }, [
    splitCount,
    isCenterZeroLabel,
    option?.bgBar?.value,
    option?.mainBar?.value,
    option?.markLine?.value,
    formatNumber?.func,
    formatNumber?.params,
    mainBarLabelPercent,
  ]);

  // 0刻度的位置，从1开始
  const zeroLabelPosition = useMemo(() => {
    /* switch (true) {
      case negativePercentage >= 0.8:
        return 6;

      case negativePercentage >= 0.6:
        return 5;

      case negativePercentage >= 0.4:
        return 4;

      case negativePercentage >= 0.2:
        return 3;

      case negativePercentage > 0:
        return 2;

      default:
        return 1;
    } */
    if (isCenterZeroLabel && negativePercentage > 0) {
      return Math.ceil(splitCount / 2) + 1;
    }
    let position = -1;
    if (negativePercentage === 0) {
      position = 1;
    } else if (negativePercentage > 0 && negativePercentage < (1 / splitCount)) {
      position = 2;
    } else if (negativePercentage === 1) {
      position = splitCount + 1;
    } else {
      position = 2 + Math?.floor(negativePercentage / (1 / splitCount));
    }

    return position > 0 ? position : 1;
  }, [negativePercentage, splitCount, isCenterZeroLabel]);

  // 刻度文案
  const xAxisLabels = useMemo(() => {
    const res = [];
    // 刻度之间的间距（去整数部分去除单位）
    let interval = getFormatNumberRes(formatNumber, max / splitCount)?.split('.')?.[0];
    // 如果转换单位后本身就是整数并且带单位
    if (SYMBOL_ARR?.includes(interval.slice(-1))) {
      interval = interval.slice(0, -1);
    }
    const maxFormatV = getFormatNumberRes(formatNumber, max);
    // 最大值转换后单位
    const unit = maxFormatV?.slice(-1);
    // 是否有单位
    const isUnit = SYMBOL_ARR?.includes(unit);
    // 小数点后0的个数
    const zeroCount = maxFormatV?.split('.')?.[1]?.length - (isUnit ? 1 : 0);
    // 保留整数的情况下，数据比较小（包括转换单位后），间隔不足10且间隔有小数时，保留一位小数
    const symbolIndex = isUnit ? SYMBOL_ARR?.indexOf(String(unit)) : -1;
    const symbolNum = isUnit ? Math.pow(10, (symbolIndex + 1) * 3) : 1; // 对应单位的数值
    const formatInterval = max / (splitCount * symbolNum); // 转换单位后去单位的间隔
    const isFirFixed = isLabelKeepInteger && (formatInterval < 10) && (Math?.ceil(formatInterval) !== formatInterval);
    for(let i = 0; i <= splitCount; i ++) {
      const labelNum = max * i / splitCount;
      let labelText: string | number = getFormatNumberRes(formatNumber, labelNum);
      labelText = isLabelKeepInteger ? labelText?.split('.')?.[0] : (isUnit ? labelText?.slice(0, -1) : labelText);
      if (isFirFixed) {
        labelText = (labelNum / symbolNum)?.toFixed(1) + (isUnit ? unit : '');
      }
      // 如果转换单位后本身就是整数并且带单位
      if (SYMBOL_ARR?.includes(labelText.slice(-1))) {
        labelText = labelText.slice(0, -1);
      }
      // 如果有负数
      if (zeroLabelPosition > 1) {
        if (isFirFixed) {
          labelText = (Number(labelText) - formatInterval * (zeroLabelPosition - 1))?.toFixed(1);
        } else {
          labelText = Number(labelText) - Number(interval) * (zeroLabelPosition - 1);
        }
        if (!isLabelKeepInteger && zeroCount > 0) {
          // 补0
          labelText = String(labelText) + '.' + String(Math.pow(10, zeroCount)).slice(1);
        }
      }
      // 有单位加上单位
      if (isUnit && Number(labelText) !== 0) {
        labelText = labelText + unit;
      }
      res.push(String(Number(labelText) === 0 ? '0' : labelText));
    }

    return res;
  }, [
    max,
    zeroLabelPosition,
    isLabelKeepInteger,
    splitCount,
    formatNumber?.func,
    formatNumber?.params,
  ]);

  // 柱子的配置
  const chartOption = useMemo(() => {
    return {
      'bgBar': {
        color: option?.bgBar?.color || '#CFE4FF',
        barWidth: option?.bgBar?.barWidth || 44,
        value: Number(option?.bgBar?.value) || 0,
      },
      'mainBar': {
        color: option?.mainBar?.color || '#5086F3',
        barWidth: option?.mainBar?.barWidth || 24,
        value: Number(option?.mainBar?.value) || 0,
      },
      'markLine': {
        color: option?.markLine?.color || '#6E7794',
        barWidth: option?.markLine?.barWidth || 44,
        value: Number(option?.markLine?.value) || 0,
      },
    };
  }, [option]);

  const getIsTooltipLeftPointer = useMemoizedFn((isTooltipHalfAdaptPosition?: boolean, pointerX?: number) => {
    if (!isTooltipHalfAdaptPosition) return false;
    if (pointerX === void 0) return false;
    const chartAreaLeft = chartAreaRef?.current?.getBoundingClientRect()?.left || 0;
    const chartAreaWidth = chartAreaRef?.current?.clientWidth || 0;
    const chartAreaCenterPointerX = chartAreaLeft + chartAreaWidth / 2;
    return pointerX > chartAreaCenterPointerX;
  });

  const { run: updateTooltipPosition, flush: updateTooltipPositionFlush } = useThrottleFn(
    (pointerX?: number, pointerY?: number, isTooltipHalfAdaptPosition?: boolean) => {
      if (pointerX === void 0 || pointerY === void 0) return;
      const tooltipWidth = tooltipRef.current?.clientWidth || 0;
      const tooltipHeight = tooltipRef.current?.clientHeight || 0;
      const isTooltipLeftPointerTmp = getIsTooltipLeftPointer(isTooltipHalfAdaptPosition, pointerX);
      const leftTmp = isTooltipLeftPointerTmp ? pointerX - 30 - tooltipWidth : pointerX + 30;
      const topTmp = pointerY - tooltipHeight / 2;
      setLeft(leftTmp);
      setTop(topTmp);
    },
    { wait: 50 },
  );

  // 柱子的宽度及left占的百分比
  const {
    bgBar,
    mainBar,
    markLine,
  } = useMemo(() => {
    const res = {
      bgBar: {
        widthPercent: 0,
        left: 0,
      },
      mainBar: {
        widthPercent: 0,
        left: 0,
        mainBarLabelLeft: 0,
      },
      markLine: {
        widthPercent: 0,
        left: 0,
      },
    };

    if (max > 0) {
      const absBgBarV = Math?.abs(chartOption?.bgBar?.value) || 0,
            absMainBarV = Math?.abs(chartOption?.mainBar?.value) || 0,
            absMarkLineV = Math?.abs(chartOption?.markLine?.value) || 0,
            bgBarUnit = chartOption?.bgBar?.value < 0 ? '-' : '',
            mainBarUnit = chartOption?.mainBar?.value < 0 ? '-' : '',
            markLineUnit = chartOption?.markLine?.value < 0 ? '-' : '';
      const bgBarWidthPercent = Math?.round((absBgBarV / max) * 10000) / 100,
            mainBarWidthPercent = Math?.round((absMainBarV / max) * 10000) / 100,
            markLineWidthPercent = Math?.round((absMarkLineV / max) * 10000) / 100;

      const intervalPercent = 100 / splitCount; // 每段占的百分比
      const zeroMovePercent = (zeroLabelPosition - 1) * intervalPercent; // 零刻度移动的百分比
      res.bgBar.widthPercent = bgBarWidthPercent < (minPercentage as number) ? (minPercentage as number) : bgBarWidthPercent;
      res.mainBar.widthPercent = mainBarWidthPercent < (minPercentage as number) ? (minPercentage as number) : mainBarWidthPercent;
      res.markLine.widthPercent = markLineWidthPercent < (minPercentage as number) ? (minPercentage as number) : markLineWidthPercent;
      // 10px间距占的容器百分比
      const mainBarLabelMarginPercent = containerBoxWidth ? (10 / containerBoxWidth) * 100 : 0;
      // 有负数的情况
      if (zeroLabelPosition > 1) {
        res.bgBar.left = bgBarUnit ? zeroMovePercent - res.bgBar.widthPercent : zeroMovePercent;
        res.mainBar.left = mainBarUnit ? zeroMovePercent - res.mainBar.widthPercent : zeroMovePercent;
        res.markLine.left = markLineUnit ? zeroMovePercent - res.markLine.widthPercent : zeroMovePercent + res.markLine.widthPercent;
        res.mainBar.mainBarLabelLeft = mainBarUnit ? 50 - (res.mainBar.widthPercent + mainBarLabelPercent) : 50 + res.mainBar.widthPercent + mainBarLabelMarginPercent;
      } else {
        res.markLine.left = res.markLine.widthPercent;
        res.mainBar.mainBarLabelLeft = res.mainBar.widthPercent + mainBarLabelMarginPercent;
      }
    }

    return res;
  }, [
    max,
    splitCount,
    zeroLabelPosition,
    minPercentage,
    chartOption?.bgBar?.value,
    chartOption?.mainBar?.value,
    chartOption?.markLine?.value,
    mainBarLabelPercent,
  ]);

  const handleMouseEnter = (e: React.MouseEvent) => {
    e?.persist();
    const pointerXTmp = e?.clientX || 0;
    const pointerYTmp = e?.clientY || 0;
    updateTooltipPosition(pointerXTmp, pointerYTmp, isTooltipHalfAdaptPosition);
    updateTooltipPositionFlush();
    setVisibleTooltip(true);
  };

  const handleMouseLeave = () => {
    setVisibleTooltip(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    e?.persist();
    const pointerXTmp = e?.clientX || 0;
    const pointerYTmp = e?.clientY || 0;
    updateTooltipPosition(pointerXTmp, pointerYTmp, isTooltipHalfAdaptPosition);
  };

  const handleResize = useCallback((e) => {
    if (chartAreaRef?.current) {
      setContainerBoxWidth(chartAreaRef?.current?.clientWidth || 0);
    }
  }, []);

  // 气泡容器添加到body最后
  useEffect(() => {
    const bubbleTipContainerEl = document.querySelector('.dbg-bullet-chart-bubble-tip-container');
    if (!bubbleTipContainerEl) {
      const newBubbleTipContainerEl = document.createElement('section');
      newBubbleTipContainerEl.className = 'dbg-bullet-chart-bubble-tip-container';
      document.body.appendChild(newBubbleTipContainerEl);
    }
    setReadyTipBox(true);
  }, []);

  // 设置第一个刻度文案
  useEffect(() => {
    if (xAxisBoxRef?.current) {
      xAxisBoxRef?.current?.setAttribute('data-bullet-chart-x-axis-after', xAxisLabels?.[0] || '-');
    }
  }, [xAxisLabels?.[0]]);

  // 因为刻度文案是居中对齐刻度的，第一个刻度左边及最后一个刻度右边的距离计算出来，给容器加这个padding
  useEffect(() => {
    const firstLabel = xAxisLabels?.[0] || '-', lastLabel = xAxisLabels?.[xAxisLabels?.length - 1] || '-';
    const li = document.createElement('li');
    li.className = 'dbg-bullet-chart-x-axis-item';
    li.style.cssText = 'position:absolute;visibility:hidden;';
    const span = document.createElement('span');
    span.className = 'dbg-bullet-chart-x-axis-item-label';
    span.style.cssText = 'position:absolute;visibility:hidden;';
    /* const {
      firstLabelWidth = 0,
      lastLabelWidth = 0,
    } = await ((): Promise<{firstLabelWidth: number, lastLabelWidth: number}> => (new Promise((resolve, reject) => {
      const t = setInterval(() => {
        if (xAxisBoxRef?.current) {
          span.innerHTML = firstLabel;
          li.appendChild(span);
          xAxisBoxRef.current.appendChild(li);
          const firstLabelWidth = span.offsetWidth || 0;
          span.innerHTML = lastLabel;
          const lastLabelWidth = span.offsetWidth || 0;
          li.removeChild(span);
          xAxisBoxRef.current.removeChild(li);
          resolve({
            firstLabelWidth,
            lastLabelWidth,
          });
          clearInterval(t);
        }
      }, 100);
    })))(); */
    const t = setInterval(() => {
      if (xAxisBoxRef?.current) {
        span.innerHTML = firstLabel;
        li.appendChild(span);
        xAxisBoxRef.current.appendChild(li);
        const firstLabelWidth = span.offsetWidth || 0;
        setContainerPaddingLeft(firstLabelWidth / 2);
        span.innerHTML = lastLabel;
        const lastLabelWidth = span.offsetWidth || 0;
        setContainerPaddingRight(lastLabelWidth / 2);
        li.removeChild(span);
        xAxisBoxRef.current.removeChild(li);
        clearInterval(t);
      }
    }, 100);
  }, [xAxisLabels?.[0], xAxisLabels?.[xAxisLabels?.length - 1]]);

  // 获取容器宽度及添加窗口大小变化获取容器宽度监听
  useEffect(() => {
    const t = setTimeout(() => {
      if (chartAreaRef?.current) {
        clearTimeout(t);
        setContainerBoxWidth(chartAreaRef?.current?.clientWidth || 0);
      }
    }, 100);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section
      className={`dbg-bullet-chart ${className}`}
      style={{
        ...style,
        // padding: `0 ${containerPaddingRight}px 0 ${containerPaddingLeft}px`,
        padding: `0`,
      }}
    >
      <section
        ref={chartAreaRef}
        className="dbg-bullet-chart-body"
        style={{height: chartOption?.bgBar?.barWidth}}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        <section
          className="dbg-bullet-chart-bg-bar"
          style={{
            backgroundColor: chartOption?.bgBar?.color,
            width: `${bgBar?.widthPercent}%`,
            left: `${bgBar?.left}%`,
            height: chartOption?.bgBar?.barWidth,
          }}
        ></section>
        <section
          className="dbg-bullet-chart-main-bar"
          style={{
            backgroundColor: chartOption?.mainBar?.color,
            width: `${mainBar.widthPercent}%`,
            left: `${mainBar.left}%`,
            height: chartOption?.mainBar?.barWidth,
          }}
        ></section>
        <label
          className="dbg-bullet-chart-main-bar-label"
          style={{
            left: `${mainBar.mainBarLabelLeft}%`,
          }}
        >{getFormatNumPlus(option?.mainBar?.value || '')}</label>
        <section
          className="dbg-bullet-chart-mark-line"
          style={{
            backgroundColor: chartOption?.markLine?.color,
            left: `${markLine?.left}%`,
            height: chartOption?.markLine?.barWidth,
          }}
        ></section>
      </section>
      <ul ref={xAxisBoxRef} data-bullet-chart-x-axis-after="" className="dbg-bullet-chart-x-axis">
        {max !== 0 && xAxisLabels?.slice(1)?.map((t: string) => {
          return (
            <li key={t} className="dbg-bullet-chart-x-axis-item">
              <span className="dbg-bullet-chart-x-axis-item-label">{t || '-'}</span>
            </li>
          );
        })}
      </ul>
      {/* 浮窗 */}
      {readyTipBox && createPortal(
        (
          <section
            ref={tooltipRef}
            className={`dbg-bullet-chart-tooltip-box ${tooltipClassName}`}
            style={{
              ...tooltipStyle,
              display: visibleTooltip ? 'block' : 'none',
              left,
              top,
            }}
          >
            {(Object.prototype.toString.call(tooltipFormatter) === '[object Function]')
              ? tooltipFormatter?.(tooltipData)
              : defaultTooltipFormatter?.(tooltipData)}
          </section>
        ),
        document.querySelector('.dbg-bullet-chart-bubble-tip-container') as Element,
      )}
    </section>
  )
}

BulletChart.defaultProps = DEFAULT_PROPS;

export default memo(BulletChart);
